import React, { useCallback, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';

import { media } from '@/utils/mixin';
import { colors } from '@/constants/theme';
import Link from '@/components/Link';
import { getSelectedFilterFromUrl } from '@/modules/CarsStock/components/common.helpers';

import { StockPagePaginationProps } from './types';

function StockPagePagination({
  page,
  total,
  itemsOnPage = 36,
  onPageClick,
  onPageNext,
  onPagePrev,
}: StockPagePaginationProps) {
  const [isStartEllipsis, setStartEllipsis] = useState<boolean>(false);
  const [isEndEllipsis, setEndEllipsis] = useState<boolean>(false);
  const [paginationList, setPaginationList] = useState<number[]>([]);
  const [finalList, setFinalList] = useState<number[]>([]);

  const router = useRouter();

  function handlerPrev() {
    onPagePrev?.();
  }

  function handlerNext() {
    onPageNext?.();
  }

  function handlerChangePaginationElement(
    event: React.MouseEvent<HTMLAnchorElement>,
    page: number
  ) {
    event.preventDefault();
    onPageClick?.(page);
  }

  const renderList = useCallback(
    (paginationList: number[]) => {
      if (paginationList.length < 7) {
        setStartEllipsis(false);
        setEndEllipsis(false);
        return paginationList.slice(1, paginationList.length - 1);
      } else if (page < 5) {
        setStartEllipsis(false);
        setEndEllipsis(true);
        return paginationList.slice(1, 5);
      } else if (page > paginationList.length - 4) {
        setStartEllipsis(true);
        setEndEllipsis(false);
        return paginationList.slice(
          paginationList.length - 5,
          paginationList.length - 1
        );
      } else {
        setStartEllipsis(true);
        setEndEllipsis(true);
        return paginationList.slice(page - 2, page + 1);
      }
    },
    [page]
  );

  useEffect(() => {
    const pageCount = Math.ceil(total / itemsOnPage);

    setPaginationList(
      [...new Array(pageCount + 1).keys()].slice(1, pageCount + 1)
    );
  }, [itemsOnPage, total]);

  useEffect(() => {
    setFinalList(renderList(paginationList));
  }, [paginationList, page, renderList]);

  if (total === 0 || total <= itemsOnPage) {
    return null;
  }

  function getPaginationLinkHref(page?: number) {
    if (router.asPath.indexOf('--page') !== -1) {
      return router.asPath.replace(
        router.asPath.substring(router.asPath.indexOf('--page')),
        page ? `--page-${page}` : ''
      );
    } else if (router.asPath.indexOf('page') !== -1) {
      return router.asPath.replace(
        router.asPath.substring(router.asPath.indexOf('page')),
        page ? `page-${page}` : ''
      );
    }

    const selectedItems = getSelectedFilterFromUrl(router.query.slug);

    return Object.keys(selectedItems).length === 0
      ? `${router.asPath + (page ? `/page-${page}` : '')}`
      : `${router.asPath + (page ? `--page-${page}` : '')}`;
  }

  return (
    <Component>
      <List>
        <PaginationBtn onClick={handlerPrev} isDisabled={page === 1}>
          {'<'}
        </PaginationBtn>
        <PaginationBtn
          onClick={handlerNext}
          isDisabled={page === paginationList.length}
        >
          {'>'}
        </PaginationBtn>

        <PaginationItem
          to={getPaginationLinkHref()}
          onClick={(event) => handlerChangePaginationElement(event, 1)}
          isActive={page === 1}
        >
          1
        </PaginationItem>

        {isStartEllipsis && <Dots>...</Dots>}

        {finalList &&
          finalList.length > 0 &&
          finalList.map((pageItem) => (
            <PaginationItem
              key={pageItem}
              to={getPaginationLinkHref(pageItem)}
              onClick={(event) =>
                handlerChangePaginationElement(event, pageItem)
              }
              isActive={page === pageItem}
            >
              {pageItem}
            </PaginationItem>
          ))}

        {isEndEllipsis && <Dots>...</Dots>}

        <PaginationItem
          to={getPaginationLinkHref(paginationList.length)}
          onClick={(event) =>
            handlerChangePaginationElement(event, paginationList.length)
          }
          isActive={page === paginationList.length}
        >
          {paginationList.length}
        </PaginationItem>
      </List>
    </Component>
  );
}

export default StockPagePagination;

const Component = styled.div`
  width: 100% !important;
  max-width: 100% !important;
`;

const List = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PaginationBtn = styled.button<{ isDisabled: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 100%;
  background: ${colors.main};
  color: ${colors.white};
  margin: 0 10px;
  border: 1px solid ${colors.main};
  font-size: 18px;
  line-height: 0;

  display: flex;
  justify-content: center;
  align-items: center;
  transition: 0.3s;

  &:hover {
    opacity: 0.7;
  }

  ${media.tablet(css`
    cursor: default;
  `)}

  ${media.mobile(css`
    margin: 0 4px;
    width: 30px;
    height: 30px;
    font-size: 16px;
  `)}
  
  ${media.mobileSmall(css`
    margin: 0 3px;
    width: 26px;
    height: 26px;
    font-size: 14px;
  `)}

  ${(props) =>
    props.isDisabled &&
    css`
      pointer-events: none;
      border-color: ${colors.gray};
      background: ${colors.gray};
    `}
`;

const PaginationItem = styled(Link)<{ isActive?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 100%;
  background: ${colors.main};
  color: ${colors.white};
  margin: 0 10px;
  border: 1px solid ${colors.main};
  font-size: 16px;
  line-height: 0;

  display: flex;
  justify-content: center;
  align-items: center;
  transition: 0.3s;

  &:hover {
    opacity: 0.7;
  }

  ${media.tablet(css`
    cursor: default;
  `)}

  ${media.mobile(css`
    margin: 0 4px;
    width: 30px;
    height: 30px;
    font-size: 14px;
  `)}
  
  ${media.mobileSmall(css`
    margin: 0 3px;
    width: 26px;
    height: 26px;
    font-size: 12px;
  `)}

  ${(props) =>
    props.isActive &&
    css`
      pointer-events: none;
      background: transparent;
      color: ${(props) => props.theme.main};
    `}
`;

const Dots = styled.span`
  font-size: 16px;
  margin: 0 10px;
  color: ${colors.main};

  ${media.mobile(css`
    font-size: 14px;
    margin: 0 4px;
  `)}

  ${media.mobileSmall(css`
    font-size: 12px;
    margin: 0 3px;
  `)}
`;
